import { Injectable } from '@angular/core';
import { Injector } from '@angular/core';
import { Http, Headers, RequestOptions, XHRBackend } from '@angular/http';
import { ConfigService } from '../../config/ConfigService';
import { SessionService } from './session.service';
import * as Globals from '../globals';
import { genericConfigValues } from '../globals';

/**
 * Handles silent renewal of the short lived OAuth access token using the refresh token issued
 * by the server at login (penetration test finding 4.2.1). The access token now lives for a few
 * minutes only; this service schedules a background refresh shortly before it expires so the user
 * session stays alive without a long lived access token.
 */
@Injectable()
export class TokenRefreshService {

    private http: Http;
    private isClientPortal: boolean = false;
    private refreshTimerId: any = null;
    private refreshPromise: Promise<string> = null;

    // Renew this many milliseconds before the access token actually expires.
    private readonly refreshSkewMs: number = 60 * 1000;
    // Fallback access token lifetime (minutes) if the server does not return expires_in.
    private readonly defaultAccessTokenLifetimeMinutes: number = 30;

    constructor(backend: XHRBackend, defaultOptions: RequestOptions, private injector: Injector) {
        this.http = new Http(backend, defaultOptions);
        this.isClientPortal = genericConfigValues.clientPortal;
    }

    /**
     * Stores the refresh token and schedules the first silent refresh. Call this right after a
     * successful login once the token response is available.
     * @param tokenResponse The raw response returned from the /token endpoint.
     */
    public startSession(tokenResponse: any): void {
        if (!tokenResponse || !tokenResponse.refresh_token) {
            return;
        }

        this.setStorageItem('refresh_token', tokenResponse.refresh_token);
        this.scheduleRefresh(this.getExpirationTime(tokenResponse.expires_in));
    }

    public resumeSession(): Promise<void> {
        const refreshToken = this.getStorageItem('refresh_token');
        if (!refreshToken) {
            return Promise.resolve();
        }

        const expiresAt = Number(this.getStorageItem('access_token_expires_at'));
        if (!expiresAt || expiresAt - this.refreshSkewMs <= Date.now()) {
            return this.refreshAccessToken().then(() => undefined, () => undefined);
        }

        this.scheduleRefresh(expiresAt);
        return Promise.resolve();
    }

    /**
     * Clears any pending refresh timer and removes the stored refresh token. Call this on logout.
     */
    public stopSession(): void {
        if (this.refreshTimerId) {
            clearTimeout(this.refreshTimerId);
            this.refreshTimerId = null;
        }
        this.removeStorageItem('refresh_token');
        this.removeStorageItem('access_token_expires_at');
    }

    public revokeSession(): void {
        const accessToken = this.getStorageItem('id_token');
        if (!accessToken || accessToken === 'Not-Set') {
            return;
        }

        const headers = new Headers();
        headers.append('Authorization', accessToken);
        headers.append('Content-Type', 'application/json');

        const config = this.injector.get(ConfigService);
        this.http.post(config.get('sampleQueryMgmtApiUrl') + 'api/Login/RevokeSession', {}, { headers: headers })
            .subscribe(() => { }, error => console.warn('Query API session revocation request failed.', error));

        this.http.post(config.get('apiUrl') + 'api/Login/RevokeSession', {}, { headers: headers })
            .subscribe(() => { }, error => console.warn('Session revocation request failed.', error));
    }

    private getExpirationTime(expiresInSeconds: number): number {
        const lifetimeMs = (expiresInSeconds && expiresInSeconds > 0)
            ? expiresInSeconds * 1000
            : this.defaultAccessTokenLifetimeMinutes * 60 * 1000;
        return Date.now() + lifetimeMs;
    }

    private scheduleRefresh(expiresAt: number): void {
        if (this.refreshTimerId) {
            clearTimeout(this.refreshTimerId);
            this.refreshTimerId = null;
        }

        let refreshInMs = expiresAt - Date.now() - this.refreshSkewMs;
        if (refreshInMs < 0) {
            refreshInMs = 0;
        }

        this.setStorageItem('access_token_expires_at', expiresAt.toString());
        this.refreshTimerId = setTimeout(() => this.refreshAccessToken().then(() => undefined, () => undefined), refreshInMs);
    }

    public hasRefreshToken(): boolean {
        return !!this.getStorageItem('refresh_token');
    }

    public refreshAccessToken(): Promise<string> {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        const refreshToken = this.getStorageItem('refresh_token');
        if (!refreshToken) {
            return Promise.reject(new Error('No refresh token is available.'));
        }

        const payload = 'grant_type=refresh_token&refresh_token=' + encodeURIComponent(refreshToken);
        const headers = new Headers();
        headers.append('Content-Type', 'application/x-www-form-urlencoded');

        const request = this.http.post(this.injector.get(ConfigService).get('authorizationServiceUrl') + 'token', payload, { headers: headers })
            .map(res => res.json())
            .toPromise()
            .then((data: any) => {
                if (!data || !data.access_token) {
                    throw new Error('The refresh response did not include an access token.');
                }

                const bearer = 'Bearer ' + data.access_token;
                Globals.setTokenKey(bearer);
                this.setStorageItem('id_token', bearer);

                if (data.refresh_token) {
                    this.setStorageItem('refresh_token', data.refresh_token);
                }

                this.scheduleRefresh(this.getExpirationTime(data.expires_in));
                return bearer;
            });

        this.refreshPromise = request.then(
            bearer => {
                this.refreshPromise = null;
                return bearer;
            },
            error => {
                this.refreshPromise = null;
                this.stopSession();
                throw error;
            });

        return this.refreshPromise;
    }

    private getStorageItem(key: string): string {
        return this.isClientPortal ? localStorage.getItem(key) : this.sessionService.getItem(key);
    }

    private setStorageItem(key: string, value: string): void {
        if (this.isClientPortal) {
            localStorage.setItem(key, value);
        } else {
            this.sessionService.setItem(key, value);
        }
    }

    private removeStorageItem(key: string): void {
        if (this.isClientPortal) {
            localStorage.removeItem(key);
        } else {
            this.sessionService.removeItem(key);
        }
    }

    private get sessionService(): SessionService {
        return this.injector.get(SessionService);
    }
}
